import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Loader2, Save, CalendarClock, Send, Star, Bell, Truck, CreditCard, PlayCircle, Zap, AlertTriangle } from 'lucide-react';
import http, { automations } from '../../lib/api';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

import OverdueSequenceCard from './OverdueSequenceCard';
const VARS = ['{customer}', '{company}', '{date}', '{time}', '{tech}', '{service}', '{reviewUrl}', '{payUrl}', '{phone}'];

const AUTOS = [
  { key: 'reminder', icon: Bell, title: 'Appointment reminders', desc: 'Sent before a scheduled job.', enable: 'reminderEnabled', tpl: 'reminderTemplate' },
  { key: 'onTheWay', icon: Truck, title: '“Tech on the way” alerts', desc: 'Sent when a technician heads out.', enable: 'onTheWayEnabled', tpl: 'onTheWayTemplate' },
  { key: 'review', icon: Star, title: 'Review requests', desc: 'Sent automatically when a job is completed.', enable: 'reviewEnabled', tpl: 'reviewTemplate' },
  { key: 'payment', icon: CreditCard, title: 'Payment-due nudges', desc: 'Used with bulk Pay Link sends.', enable: 'paymentDueEnabled', tpl: 'paymentDueTemplate' },
];

export default function AutomationsPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reminderDate, setReminderDate] = useState(() => todayISO());
  const [running, setRunning] = useState(false);
  const [rules, setRules] = useState([]);
  const [busyRule, setBusyRule] = useState('');

  useEffect(() => { automations.config().then(setCfg).catch(() => setCfg({})); }, []);
  const loadRules = () => automations.catalog().then(setRules).catch(() => setRules([]));
  useEffect(() => { loadRules(); }, []);

  // "Run now" is a real run, regardless of the toggle - not a dry run. A rule
  // that messages customers asks first, and the server refuses it unconfirmed.
  const previewRule = async (key) => {
    const rule = rules.find((x) => x.key === key);
    if (rule?.touchesCustomers && !window.confirm(
      `"${rule.name}" will send real texts/emails to customers right now. Run it?`)) return;
    setBusyRule(key);
    try {
      const r = rule?.touchesCustomers
        ? (await http.post(`/automations/${key}/run`, { confirm: true })).data
        : await automations.runOne(key);
      const summary = Object.entries(r.result || {}).map(([k, v]) => `${k}: ${v}`).join(' · ');
      if (r.ok) toast.success(summary || 'Nothing to do');
      else toast.error(r.result?.error || 'That rule failed');
      loadRules();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not run that rule'); }
    finally { setBusyRule(''); }
  };
  const upd = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { const c = await automations.save(cfg); setCfg(c); toast.success('Automations saved'); }
    catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const runReminders = async () => {
    // workorder dates are MM/DD/YYYY
    const [y, m, d] = reminderDate.split('-');
    setRunning(true);
    try {
      const r = await automations.runReminders(`${m}/${d}/${y}`);
      toast.success(`Reminders: ${r.sent} sent · ${r.skipped} skipped (of ${r.total} jobs)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not run reminders'); }
    finally { setRunning(false); }
  };

  if (cfg === null) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5" data-testid="automations-panel">
      <Card className="p-5 border-slate-200">
        <div className="flex items-center gap-2 font-bold text-slate-900"><Star className="w-4 h-4 text-amber-500" />Google Review Link</div>
        <p className="text-sm text-slate-500 mt-1">Paste your Google review link — it is inserted as <code>{'{reviewUrl}'}</code> in review messages.</p>
        <Input value={cfg.googleReviewUrl || ''} onChange={(e) => upd('googleReviewUrl')(e.target.value)} placeholder="https://g.page/r/your-business/review" className="mt-3 h-10" data-testid="google-review-url" />
      </Card>

      {AUTOS.map((a) => (
        <Card key={a.key} className="p-5 border-slate-200" data-testid={`automation-${a.key}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#8BC53F]/10 flex items-center justify-center flex-shrink-0"><a.icon className="w-4 h-4 text-[#6FA82C]" /></div>
              <div>
                <div className="font-bold text-slate-900">{a.title}</div>
                <p className="text-xs text-slate-500">{a.desc}</p>
              </div>
            </div>
            <Switch checked={!!cfg[a.enable]} onCheckedChange={(v) => upd(a.enable)(v)} data-testid={`toggle-${a.key}`} />
          </div>
          <Textarea value={cfg[a.tpl] || ''} onChange={(e) => upd(a.tpl)(e.target.value)} rows={2} className="mt-3 text-sm" data-testid={`template-${a.key}`} />
        </Card>
      ))}

      <Card className="p-4 border-slate-200">
        <div className="text-xs text-slate-500 mb-2">Available variables (click area to copy manually):</div>
        <div className="flex flex-wrap gap-1.5">{VARS.map((v) => <span key={v} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-mono">{v}</span>)}</div>
      </Card>

      <Card className="p-5 border-slate-200" data-testid="daily-autorun-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#0d2818]/10 flex items-center justify-center flex-shrink-0"><CalendarClock className="w-4 h-4 text-[#0d2818]" /></div>
            <div>
              <div className="font-bold text-slate-900">Auto-send daily reminders</div>
              <p className="text-xs text-slate-500">Automatically text/email reminders for every job scheduled that day — no clicking required.</p>
            </div>
          </div>
          <Switch checked={!!cfg.dailyReminderEnabled} onCheckedChange={(v) => upd('dailyReminderEnabled')(v)} data-testid="toggle-daily-autorun" />
        </div>
        {cfg.dailyReminderEnabled && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <span>Send each day at</span>
            <select value={cfg.dailyReminderHour ?? 8} onChange={(e) => upd('dailyReminderHour')(Number(e.target.value))} className="h-9 border border-slate-200 rounded-md px-2 text-sm" data-testid="daily-autorun-hour">
              {Array.from({ length: 14 }, (_, i) => i + 5).map((h) => <option key={h} value={h}>{h > 12 ? h - 12 : h}:00 {h >= 12 ? 'PM' : 'AM'}</option>)}
            </select>
            <span className="text-xs text-slate-400">Pacific time · requires “Appointment reminders” on above.</span>
          </div>
        )}
      </Card>


      <Card className="p-5 border-slate-200" data-testid="scheduled-rules-card">
        <div className="flex items-start gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-[#0d2818]/10 flex items-center justify-center flex-shrink-0"><Zap className="w-4 h-4 text-[#0d2818]" /></div>
          <div>
            <div className="font-bold text-slate-900">Scheduled rules</div>
            <p className="text-xs text-slate-500">Run once a day, after the hour set above. “Run now” runs a rule once for real, even while it is switched off; rules that message customers ask first.</p>
          </div>
        </div>
        <div className="mt-3 divide-y divide-slate-100">
          {rules.map((r) => (
            <div key={r.key} className="py-3 flex items-start justify-between gap-4" data-testid={`rule-${r.key}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono text-slate-400">#{r.num}</span>
                  <span className="font-semibold text-slate-900 text-sm">{r.name}</span>
                  {r.touchesCustomers && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                      <AlertTriangle className="w-3 h-3" />messages customers
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {r.lastRun ? `Last run ${r.lastRun}` : 'Never run'}
                  {r.lastResult?.result && ` · ${Object.entries(r.lastResult.result).map(([k, v]) => `${k}: ${v}`).join(' · ')}`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" className="gap-1 h-8" disabled={busyRule === r.key}
                        onClick={() => previewRule(r.key)} data-testid={`preview-${r.key}`}>
                  {busyRule === r.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}Run now
                </Button>
                <Switch checked={!!cfg[`${r.key}Enabled`]} onCheckedChange={upd(`${r.key}Enabled`)} data-testid={`toggle-${r.key}`} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">Toggling a rule takes effect when you press Save. Rules marked “messages customers” send real SMS or email — preview first.</p>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Card className="p-3 border-slate-200 flex items-center gap-2" data-testid="run-reminders-card">
          <CalendarClock className="w-4 h-4 text-slate-500" />
          <Input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} className="h-9 w-40" data-testid="reminder-date" />
          <Button variant="outline" onClick={runReminders} disabled={running} className="gap-1.5" data-testid="run-reminders-btn">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send reminders for this day
          </Button>
        </Card>
        <Button onClick={save} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="save-automations-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save Automations
        </Button>
      </div>
      <OverdueSequenceCard cfg={cfg} upd={upd} onSave={save} saving={saving} />
      <p className="text-xs text-slate-400">Reminders send via SMS when Twilio is connected, otherwise email (Resend). Connect channels in Communications.</p>
    </div>
  );
}
